import { useEffect, useMemo, useState } from "react";
import { assignUserRole, getUserRoles, setUserRoleStatus } from "../../services/auth";
import { checkIsRestaurantAdmin, SALON_ORDER_ROLE_ID } from "../../services/checkRole";
import { isSuperAdmin } from "../../utils/Util";
import { getRestaurantStaff, IStaffMember } from "../services/staffService";
import CreateWaiterModal from "./CreateWaiterModal";

function initials(name: string, surname: string): string {
  return `${name?.[0] ?? ""}${surname?.[0] ?? ""}`.toUpperCase() || "?";
}

const AVATAR_COLORS = ["#ea580c", "#2563eb", "#16a34a", "#7c3aed", "#0891b2", "#d97706"];
function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash += seed.charCodeAt(i);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/** Salon Siparişi yetkisi: rol satırı yok / var ama kapalı / açık */
type SalonAccess = "none" | "inactive" | "active";

function salonAccess(w: IStaffMember): SalonAccess {
  const row = w.roles.find((r) => r.roleId?.toLowerCase() === SALON_ORDER_ROLE_ID);
  if (!row) return "none";
  return row.isEnabled ? "active" : "inactive";
}

/** Garsonun ek rolleri — garson rolünün kendisi ve salon rolü ayrıca gösterildiği için hariç */
const EXTRA_ROLE_LABEL: Record<string, string> = {
  ReadRestorantOrderDetail: "Sipariş detayı okuma",
};

type Filter = "all" | "withAccess" | "withoutAccess";

function apiError(err: any, fallback: string): string {
  const data = err?.response?.data;
  return (Array.isArray(data) && data[0]?.description) || fallback;
}

interface Props {
  restaurantId: string;
}

export default function WaitersTab({ restaurantId }: Props) {
  const [waitersAll, setWaitersAll] = useState<IStaffMember[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  // Garson açma ucu restoranı çağıranın token'ından çözüyor; yalnız RestourantAdmin kullanabilir.
  const [canCreate, setCanCreate] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [notice, setNotice] = useState("");
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);

  // Rol atamasını kapatmak/yeniden açmak atama kaydının id'sini ister. O id'yi yalnız SuperAdmin
  // okuyabiliyor (UserRole/RolesByUserId); personel listesi id'yi döndürmüyor. Bu yüzden restoran
  // yöneticisi yetki verebilir ama geri alamaz.
  const superAdmin = useMemo(() => isSuperAdmin(), []);

  const load = () => {
    setLoading(true);
    setError("");
    getRestaurantStaff(restaurantId)
      .then((res) => setWaitersAll(res.data?.waiters ?? []))
      .catch((err) => { setWaitersAll(null); setError(apiError(err, "Garson listesi alınamadı.")); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  useEffect(() => {
    checkIsRestaurantAdmin().then(setCanCreate).catch(() => setCanCreate(false));
  }, []);

  /* ── Yetki işlemleri ── */

  const grant = async (w: IStaffMember) => {
    setBusyUserId(w.userId);
    setError("");
    setNotice("");
    try {
      await assignUserRole(w.userId, SALON_ORDER_ROLE_ID);
      setNotice(`${w.customerName} ${w.customerSurName} için Salon Siparişi yetkisi verildi. Açık oturumu kapatıldı; yeniden giriş yapması gerekiyor.`);
      load();
    } catch (err) {
      setError(apiError(err, "Yetki verilemedi."));
    } finally {
      setBusyUserId(null);
    }
  };

  /** Yalnız SuperAdmin: atama kaydını bulup aç/kapa */
  const setActive = async (w: IStaffMember, enable: boolean) => {
    setBusyUserId(w.userId);
    setError("");
    setNotice("");
    try {
      const res = await getUserRoles(w.userId);
      const row = (res.data ?? []).find((r) => r.roleId?.toLowerCase() === SALON_ORDER_ROLE_ID);
      const userRoleId = row?.id ?? (row as any)?.userRoleId;
      if (!userRoleId) throw new Error("Rol ataması bulunamadı.");
      await setUserRoleStatus(userRoleId, enable);
      setConfirmRevokeId(null);
      setNotice(enable
        ? `${w.customerName} ${w.customerSurName} için Salon Siparişi yetkisi yeniden açıldı.`
        : `${w.customerName} ${w.customerSurName} için Salon Siparişi yetkisi kaldırıldı. Açık oturumu kapatıldı.`);
      load();
    } catch (err) {
      setError(apiError(err, enable ? "Yetki yeniden açılamadı." : "Yetki kaldırılamadı."));
    } finally {
      setBusyUserId(null);
    }
  };

  /* ── Liste ── */

  const mine = useMemo(() => waitersAll ?? [], [waitersAll]);
  const withoutAccessCount = mine.filter((w) => salonAccess(w) !== "active").length;

  const waiters = useMemo(() => {
    let list = mine;
    if (filter === "withAccess") list = list.filter((w) => salonAccess(w) === "active");
    if (filter === "withoutAccess") list = list.filter((w) => salonAccess(w) !== "active");
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return list;
    return list.filter((u) =>
      `${u.customerName} ${u.customerSurName}`.toLocaleLowerCase("tr").includes(q) ||
      (u.customerUserName ?? "").toLocaleLowerCase("tr").includes(q) ||
      (u.customerTelNo ?? "").includes(q) ||
      (u.customerEmail ?? "").toLocaleLowerCase("tr").includes(q)
    );
  }, [mine, filter, search]);

  const activeCount = mine.filter((w) => w.customerIsEnabled && (w.linkIsEnabled ?? true)).length;

  const renderAccess = (w: IStaffMember) => {
    const access = salonAccess(w);
    const busy = busyUserId === w.userId;

    const badge = access === "active"
      ? <span className="dinein-badge dinein-badge--paid"><i className="bi bi-shop-window me-1"></i>Salon yetkisi var</span>
      : access === "inactive"
        ? <span className="dinein-badge dinein-badge--cancelled"><i className="bi bi-slash-circle me-1"></i>Salon yetkisi kapalı</span>
        : <span className="dinein-badge" style={{ background: "#fef3c7", color: "#92400e" }}><i className="bi bi-exclamation-triangle me-1"></i>Salon yetkisi yok</span>;

    let action: JSX.Element | null = null;
    if (access === "none") {
      action = (
        <button className="dinein-btn dinein-btn--primary dinein-btn--sm" disabled={busy} onClick={() => grant(w)}>
          {busy ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-shield-plus me-1"></i>Yetki ver</>}
        </button>
      );
    } else if (access === "active") {
      action = superAdmin ? (
        confirmRevokeId === w.userId ? (
          <span style={{ display: "inline-flex", gap: 6 }}>
            <button className="dinein-btn dinein-btn--danger dinein-btn--sm" disabled={busy} onClick={() => setActive(w, false)}>
              {busy ? <span className="spinner-border spinner-border-sm" /> : "Evet, kaldır"}
            </button>
            <button className="dinein-btn dinein-btn--ghost dinein-btn--sm" disabled={busy} onClick={() => setConfirmRevokeId(null)}>Vazgeç</button>
          </span>
        ) : (
          <button className="dinein-btn dinein-btn--ghost dinein-btn--sm" onClick={() => setConfirmRevokeId(w.userId)}>
            <i className="bi bi-shield-x me-1"></i>Yetkiyi kaldır
          </button>
        )
      ) : (
        <span className="dinein-small dinein-muted" title="Yetkiyi kaldırma işlemi şu an yalnız sistem yöneticisi tarafından yapılabiliyor.">
          <i className="bi bi-info-circle me-1"></i>Kaldırmak için sistem yöneticisi
        </span>
      );
    } else {
      action = superAdmin ? (
        <button className="dinein-btn dinein-btn--primary dinein-btn--sm" disabled={busy} onClick={() => setActive(w, true)}>
          {busy ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-shield-check me-1"></i>Yeniden aç</>}
        </button>
      ) : (
        <span className="dinein-small dinein-muted" title="Kapatılmış yetkiyi yeniden açma şu an yalnız sistem yöneticisi tarafından yapılabiliyor.">
          <i className="bi bi-info-circle me-1"></i>Yeniden açmak için sistem yöneticisi
        </span>
      );
    }

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
        {badge}
        <span style={{ marginLeft: "auto" }}>{action}</span>
      </div>
    );
  };

  return (
    <div className="waiters">
      <div className="waiters__toolbar">
        <div className="dinein-search" style={{ flex: 1, marginBottom: 0, minWidth: 180 }}>
          <i className="bi bi-search"></i>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Garson ara (ad, telefon, e-posta)..." />
          {search && <button onClick={() => setSearch("")}><i className="bi bi-x-lg"></i></button>}
        </div>
        {canCreate && (
          <button className="dinein-btn dinein-btn--primary" onClick={() => { setNotice(""); setShowCreate(true); }}>
            <i className="bi bi-person-plus-fill me-1"></i>Garson Ekle
          </button>
        )}
        <button className="dinein-btn dinein-btn--ghost" onClick={load} disabled={loading}>
          <i className={`bi bi-arrow-clockwise${loading ? " dinein-spin" : ""} me-1`}></i>Yenile
        </button>
      </div>

      {error && (
        <div className="dinein-alert dinein-alert--error">
          <i className="bi bi-exclamation-circle-fill"></i><span>{error}</span>
          <button type="button" onClick={() => setError("")}><i className="bi bi-x-lg"></i></button>
        </div>
      )}

      {notice && (
        <div className="dinein-alert dinein-alert--success">
          <i className="bi bi-check-circle-fill"></i><span>{notice}</span>
          <button type="button" onClick={() => setNotice("")}><i className="bi bi-x-lg"></i></button>
        </div>
      )}

      {!loading && withoutAccessCount > 0 && (
        <div className="dinein-alert dinein-alert--warn">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <span>
            <b>{withoutAccessCount} garsonun</b> Salon Siparişi yetkisi yok. Bu yetki olmadan garson giriş yapabilir ama salona erişemez.
            {" "}Kartındaki <b>Yetki ver</b> ile açın.
          </span>
        </div>
      )}

      {loading && !waitersAll ? (
        <div className="dinein-center dinein-center--tall"><span className="spinner-border" /></div>
      ) : mine.length === 0 ? (
        <div className="dinein-center dinein-center--tall">
          <i className="bi bi-person-badge"></i>
          <div>Bu restorana bağlı garson bulunmuyor.</div>
          {canCreate ? (
            <button className="dinein-btn dinein-btn--primary" style={{ marginTop: 10 }}
              onClick={() => { setNotice(""); setShowCreate(true); }}>
              <i className="bi bi-person-plus-fill me-1"></i>İlk garsonu ekle
            </button>
          ) : (
            <div className="dinein-small dinein-muted">Garson hesapları restoran yöneticisi tarafından açılır.</div>
          )}
        </div>
      ) : (
        <>
          <div className="waiters__summary" style={{ flexWrap: "wrap" }}>
            <span><strong>{mine.length}</strong> garson</span>
            <span className="dinein-muted">·</span>
            <span><strong style={{ color: "#16a34a" }}>{activeCount}</strong> aktif</span>
            <span className="dinein-muted">·</span>
            <span><strong style={{ color: "#0f766e" }}>{mine.length - withoutAccessCount}</strong> salon yetkili</span>
            <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
              {([["all", "Tümü"], ["withAccess", "Yetkili"], ["withoutAccess", "Yetkisiz"]] as [Filter, string][]).map(([k, label]) => (
                <button key={k} type="button"
                  className={`dinein-btn dinein-btn--sm ${filter === k ? "dinein-btn--primary" : "dinein-btn--ghost"}`}
                  onClick={() => setFilter(k)}>
                  {label}
                </button>
              ))}
            </span>
          </div>

          {waiters.length === 0 ? (
            <div className="dinein-center" style={{ padding: "30px 0" }}>
              <div className="dinein-muted">{search ? `"${search}" ile eşleşen garson yok.` : "Bu filtrede garson yok."}</div>
            </div>
          ) : (
            <div className="waiters__grid">
              {waiters.map((w) => {
                const enabled = w.customerIsEnabled && (w.linkIsEnabled ?? true);
                const fullName = `${w.customerName ?? ""} ${w.customerSurName ?? ""}`.trim() || w.customerUserName;
                const extraRoles = w.roles.filter((r) => r.isEnabled && EXTRA_ROLE_LABEL[r.roleName]);
                return (
                  <div key={w.userId} className={`waiter-card${enabled ? "" : " waiter-card--off"}`} style={{ flexWrap: "wrap" }}>
                    <div className="waiter-card__avatar" style={{ background: avatarColor(w.userId) }}>
                      {initials(w.customerName, w.customerSurName)}
                    </div>
                    <div className="waiter-card__main">
                      <div className="waiter-card__name">{fullName}</div>
                      {w.customerUserName && w.customerUserName !== fullName && (
                        <div className="waiter-card__user"><i className="bi bi-person me-1"></i>{w.customerUserName}</div>
                      )}
                      <div className="waiter-card__contact">
                        {w.customerTelNo && <span><i className="bi bi-telephone me-1"></i>{w.customerTelNo}</span>}
                        {w.customerEmail && <span><i className="bi bi-envelope me-1"></i>{w.customerEmail}</span>}
                      </div>
                      {extraRoles.length > 0 && (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                          {extraRoles.map((r) => (
                            <span key={r.roleId} className="dinein-badge" style={{ background: "#f1f5f9", color: "#475569" }}>
                              {EXTRA_ROLE_LABEL[r.roleName]}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <span className={`dinein-badge ${enabled ? "dinein-badge--paid" : "dinein-badge--cancelled"}`}>
                      {enabled ? "Aktif" : "Pasif"}
                    </span>
                    <div style={{ flexBasis: "100%" }}>{renderAccess(w)}</div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="dinein-hint" style={{ marginTop: 12 }}>
            <i className="bi bi-info-circle me-1"></i>
            Garsonun salona girebilmesi için hem hesabının aktif olması hem de <b>Salon Siparişi yetkisi</b> gerekir.
            Yetki değişince garsonun açık oturumu kapanır; yeniden giriş yapması gerekir.
          </div>
        </>
      )}

      {showCreate && (
        <CreateWaiterModal
          restaurantId={restaurantId}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setNotice("Garson hesabı oluşturuldu ve Salon Siparişi yetkisi verildi."); load(); }}
        />
      )}
    </div>
  );
}
